import {
  isNeedsAttentionPaymentInstructionStatus,
  PayinStatus,
  PAYMENT_INSTRUCTION_STATUS_GROUP,
  PaymentInstructionStatus,
  PaymentInstructionStatusGroup,
  TERMINAL_PAYMENT_INSTRUCTION_STATUSES
} from '@sollapay/enums';
import {parseMoney} from '@sollapay/utils/money';

import type {BuyerPaymentStatus} from '@sollapay/types';

export function isFailedPaymentInstructionStatus(
  status: PaymentInstructionStatus
): status is PaymentInstructionStatus.FAILED | PaymentInstructionStatus.REJECTED {
  return status === PaymentInstructionStatus.FAILED || status === PaymentInstructionStatus.REJECTED;
}

/** True for any status not in a terminal failure/cancellation state (COMPLETED included). */
export function isNonTerminalInstructionStatus(status: PaymentInstructionStatus): boolean {
  return !TERMINAL_PAYMENT_INSTRUCTION_STATUSES.includes(status);
}

export interface PaymentInstructionCollectionInput {
  status: PaymentInstructionStatus;
  allocatedAmountNis: string;
}

/** Sum of allocatedAmountNis for instructions actually collected (COMPLETED or PARTIALLY_COMPLETED). */
export function computeDepositPaidNis(instructions: PaymentInstructionCollectionInput[]): string {
  return instructions
    .filter(
      i =>
        i.status === PaymentInstructionStatus.COMPLETED ||
        i.status === PaymentInstructionStatus.PARTIALLY_COMPLETED
    )
    .reduce((sum, i) => sum.add(parseMoney(i.allocatedAmountNis)), parseMoney('0'))
    .toString();
}

/** True once the paid deposit meets or exceeds the full obligation (overpayment still counts) */
export function isPaymentFullyCollected(
  depositPaidNis: string,
  maxCollectableNis: string
): boolean {
  return parseMoney(depositPaidNis).gte(maxCollectableNis);
}

/**
 * Denominator is always the full obligation (`maxCollectableNis`), never the displayed
 * "amount due" figure — a general-sale buyer/purchase showing a shrinking outstanding
 * balance still needs "fully collected" measured against the whole obligation, not
 * against what's left.
 */
export function deriveBuyerPaymentStatus(
  instructions: PaymentInstructionCollectionInput[],
  depositPaidNis: string,
  maxCollectableNis: string
): BuyerPaymentStatus {
  if (instructions.length === 0) return 'no_request';

  const active = instructions.filter(i => isNonTerminalInstructionStatus(i.status));
  if (active.length === 0) return 'no_active_request';

  if (
    active.some(
      i =>
        i.status === PaymentInstructionStatus.COMPLETED ||
        i.status === PaymentInstructionStatus.PARTIALLY_COMPLETED
    )
  ) {
    return isPaymentFullyCollected(depositPaidNis, maxCollectableNis)
      ? 'funds_held'
      : 'partial_held';
  }

  return 'request_sent';
}

export interface PaymentInstructionSummaryInput {
  status: PaymentInstructionStatus;
  amount: string;
  allocatedAmountNis: string;
  handledAt?: string | null;
}

export interface PaymentInstructionGroupSummary {
  count: number;
  amount: string;
}

export interface PaymentInstructionsSummary {
  counts: Record<PaymentInstructionStatusGroup, number> & {all: number};
  inProgress: PaymentInstructionGroupSummary;
  needsAttention: {count: number};
  completed: PaymentInstructionGroupSummary;
}

/** A needs-attention instruction the firm already marked as handled — no longer awaiting action. */
export function isHandledPaymentInstruction(
  instruction: Pick<PaymentInstructionSummaryInput, 'status' | 'handledAt'>
): boolean {
  return (
    isNeedsAttentionPaymentInstructionStatus(instruction.status) && Boolean(instruction.handledAt)
  );
}

/**
 * Curated KPI/pill totals for an instructions list (e.g. the trust account Instructions tab).
 * "In progress" sums the expected `amount` (a PARTIALLY_COMPLETED request still counts in full);
 * "completed" sums `allocatedAmountNis` (what was actually received).
 */
export function summarizePaymentInstructions(
  instructions: PaymentInstructionSummaryInput[]
): PaymentInstructionsSummary {
  const counts: Record<PaymentInstructionStatusGroup, number> & {all: number} = {
    [PaymentInstructionStatusGroup.IN_PROGRESS]: 0,
    [PaymentInstructionStatusGroup.NEEDS_ATTENTION]: 0,
    [PaymentInstructionStatusGroup.COMPLETED]: 0,
    all: instructions.length
  };

  let inProgressAmount = parseMoney(0);
  let completedAmount = parseMoney(0);

  for (const instruction of instructions) {
    if (isHandledPaymentInstruction(instruction)) continue;

    const group = PAYMENT_INSTRUCTION_STATUS_GROUP[instruction.status];
    counts[group] += 1;

    if (group === PaymentInstructionStatusGroup.IN_PROGRESS) {
      inProgressAmount = inProgressAmount.add(instruction.amount);
    }

    if (group === PaymentInstructionStatusGroup.COMPLETED) {
      completedAmount = completedAmount.add(instruction.allocatedAmountNis);
    }
  }

  return {
    counts,
    inProgress: {
      count: counts[PaymentInstructionStatusGroup.IN_PROGRESS],
      amount: inProgressAmount.toString()
    },
    needsAttention: {count: counts[PaymentInstructionStatusGroup.NEEDS_ATTENTION]},
    completed: {
      count: counts[PaymentInstructionStatusGroup.COMPLETED],
      amount: completedAmount.toString()
    }
  };
}

/**
 * Where an instruction goes when one of its NEEDS_REVIEW payins is rejected, from what its other
 * payins say: another review still pending, money settled, or a provider payment in transit.
 */
export function statusWithoutRejectedPayin(params: {
  instructionAmount: string;
  otherPayins: {status: PayinStatus; amount: string; appliedAmount: string | null}[];
}): PaymentInstructionStatus {
  const {instructionAmount, otherPayins} = params;
  const has = (status: PayinStatus) => otherPayins.some(p => p.status === status);
  if (has(PayinStatus.NEEDS_REVIEW)) return PaymentInstructionStatus.NEEDS_REVIEW;

  const settledAmount = otherPayins
    .filter(p => p.status === PayinStatus.SETTLED)
    .reduce((sum, p) => sum.add(parseMoney(p.appliedAmount ?? p.amount)), parseMoney('0'));
  if (settledAmount.gte(instructionAmount)) return PaymentInstructionStatus.COMPLETED;
  if (has(PayinStatus.ACCEPTED) || has(PayinStatus.PARTIALLY_ACCEPTED)) {
    return PaymentInstructionStatus.FUNDS_IN_TRANSIT;
  }
  return settledAmount.isZero()
    ? PaymentInstructionStatus.AWAITING_FUNDS
    : PaymentInstructionStatus.PARTIALLY_COMPLETED;
}

/**
 * Whether rejecting a payin should void the instruction's inflight hold. There is one hold per
 * instruction: a statement-created payin never owns it, and another provider payment still relies
 * on it while in transit or in review.
 */
export function rejectReleasesInflight(params: {
  rejectedIsFromStatement: boolean;
  otherPayins: {status: PayinStatus; isCreatedFromStatement(): boolean}[];
}): boolean {
  const {rejectedIsFromStatement, otherPayins} = params;
  if (rejectedIsFromStatement) return false;
  return !otherPayins.some(
    p =>
      p.status === PayinStatus.ACCEPTED ||
      p.status === PayinStatus.PARTIALLY_ACCEPTED ||
      (p.status === PayinStatus.NEEDS_REVIEW && !p.isCreatedFromStatement())
  );
}

/**
 * Share of buyer-resolved PIS requests that were paid rather than left to expire, as a percentage
 * with one decimal. Null while nothing has resolved yet.
 */
export function computePisCompletionRatePct(counts: {
  paidCount: number;
  expiredCount: number;
}): number | null {
  const resolved = counts.paidCount + counts.expiredCount;
  if (resolved === 0) return null;
  return Math.round((counts.paidCount / resolved) * 1000) / 10;
}
